import { downloadFinished, downloadStarted, folderLoading, folderPageLoaded, folderSelected, initialLoad, } from '../../redux/slices/downloadCenterSlice';
import { getDownloadCenterFiles, downloadFiles, } from '../../utils/api/downloadCenter';
import useDownloadCenterPagination from './useDownloadCenterPagination';
import useDownloadCenterSelection from './useDownloadCenterSelection';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';

// This hook manages the overall state and actions related to the Download Center, 
// including loading files, handling folder selection, managing pagination, and initiating downloads.

const useDownloadCenterFiles = () => {
  const dispatch = useDispatch();
  const state = useSelector((reduxState) => reduxState.downloadCenter);
  const { 
    activeFolder, folders, folderPages, isDownloading, paginationHistory, selectedFileNames, 
  } = state;

  console.log('Download Center State:', state); // Debugging log to check the current state of the Download Center

  // Initial load of folders and files when the component mounts
  useEffect(() => {
    const fetchFiles = async () => {
      const { data } = await getDownloadCenterFiles();
      dispatch(initialLoad(data));
    };

    fetchFiles();
  }, [dispatch]);

  const activeFolderPage = folderPages[activeFolder] || {};
  const visibleFiles = useMemo(() => activeFolderPage.files || [], [activeFolderPage.files]);
  const isLoadingFiles = !!activeFolderPage.isLoading;

  // Function to load a specific page of files for a folder, using the continuation token for pagination
  const loadFolderPage = async (folderName, continuationToken) => {
    dispatch(folderLoading(folderName));
    const { data } = await getDownloadCenterFiles({ folderName, continuationToken });
    dispatch(folderPageLoaded({ folderName, data }));
  };

  // Function to handle when a user selects a folder, which triggers loading the first page of that folder's files if not already loaded
  const handleFolderSelect = async (folderName) => { 
    dispatch(folderSelected(folderName));
    if (folderPages[folderName]) return;
    await loadFolderPage(folderName);
  };

  // Hook for managing file selection in the download center
  const { 
    handleFileSelect, selectedCount, selectedFiles,
  } = useDownloadCenterSelection({ dispatch, selectedFileNames, visibleFiles });

  // Hook for managing pagination logic in the download center
  const { 
    handleNextPage, handlePreviousPage, hasNextPage, hasPreviousPage,
  } = useDownloadCenterPagination({
    activeFolder, activeFolderPage, dispatch, isLoadingFiles, loadFolderPage, paginationHistory,
  });

  // Function to handle initiating the download of selected files, which dispatches actions to update the state accordingly
  const handleDownload = async () => {
    if (selectedCount === 0 || isDownloading) return;
    dispatch(downloadStarted());

    try {
      await downloadFiles(selectedFiles);
    } finally {
      dispatch(downloadFinished());
    }
  };

  return {
    activeFolder, folders, handleDownload, handleFileSelect, handleFolderSelect, handleNextPage,
    handlePreviousPage, hasNextPage, hasPreviousPage, isLoadingFiles, isDownloading,
    selectedCount, selectedFileNames, visibleFiles,
  };
};

export default useDownloadCenterFiles;
